import type { NsDict } from '../ru';

export const profile: NsDict<'profile'> = {
  loading: 'Loading profile…',
  'crumb.admin': 'Administration',
  'crumb.users': 'Users',
  'tab.stats': 'Statistics',
  'tab.games': 'Games · {n}',
  'tab.settings': 'Settings',
  'tag.banned': 'banned',
  'tag.online': 'online',
  since: 'Member since {date} · last active {seen}',
  seenNow: 'now',
  'method.login': 'username {login}',
  bannedAt: 'Banned {date}',
  bannedAtReason: 'Banned {date}: {reason}',
  balance: 'Balance',
  ban: 'Ban',
  unban: 'Unban',
  'ban.title': 'Ban {name}',
  'ban.text': 'The player will be disconnected and will not be able to sign in. In an active game their turn will time out; the seat is freed after the game.',
  'ban.reason': 'Reason (visible to the player)',
  'ban.placeholder': 'e.g. rule violation',

  'stats.summary': 'Summary',
  'stats.games': 'Games',
  'stats.gamesHint': 'cancelled among them: {n}',
  'stats.winRate': 'Win rate',
  'stats.winRateHint': 'share of games with a payout among those completed',
  'stats.net': 'Net result',
  'stats.netHint': 'sum of net results, cr',
  'stats.bestWin': 'Best win',
  'stats.correctDirection': 'Correct direction',
  'stats.avgError': 'Average error',
  'stats.avgErrorHint': 'when the direction was correct',
  'stats.pointsValue': '{v} pts',
  'stats.folds': 'Folds',
  'stats.restores': 'Balance resets',
  'stats.restoresHint': 'how many times the balance was returned to the starting amount',
  'stats.daily': 'Result by day',
  'stats.dailyLabel': 'Net result by day',
  'stats.dailyNote': 'Last {n} day with games. Hover over a bar to see the amount.|Last {n} days with games. Hover over a bar to see the amount.',

  'settings.visibility': 'Visibility at the table',
  'settings.statsShown': 'Win rate and net result are visible at the table',
  'settings.statsHidden': 'Statistics hidden',
  'settings.showStats': 'Show win rate and net result on my nameplate',
  'settings.showStatsNote': 'Other players will see them next to your name in the list of participants.',
  'settings.winRateTitle': 'win rate',
  'settings.statsHiddenTag': 'statistics hidden',
  'settings.name': 'Name at the table',
  'settings.nameChanged': 'Name changed',
  'settings.save': 'Save',
  'settings.nameNote': 'Past games keep the name you played them under.',
  'settings.changePassword': 'Change password',
  'settings.setPassword': 'Sign in with username and password',
  'settings.passwordMismatch': 'passwords do not match',
  'settings.passwordSaved': 'Password saved',
  'settings.login': 'Username',
  'settings.currentPassword': 'Current password',
  'settings.newPassword': 'New password',
  'settings.newPasswordAgain': 'Repeat new password',
  'settings.savePassword': 'Save password',
  'settings.providers': 'Sign in with services',
  'settings.noProviders': 'Sign-in with external services is not configured on the server.',
  'settings.linkedSince': 'since {date}',
  'settings.unlinked': 'Service unlinked',
  'settings.unlink': 'Unlink',
  'settings.notLinked': 'not linked',
  'settings.link': 'Link',

  'reset.button': 'Reset balance',
  'reset.already': 'the balance is already at the starting amount',
  'reset.title': 'Reset balance',
  'reset.titleFor': 'Reset balance: {name}',
  'reset.confirm': 'Reset to {amount|cr}',
  'reset.textBefore': 'The balance will become',
  'reset.textNow': '(currently',
  'reset.textAfter': '). Statistics and game history are kept. Resetting is not available during a game.',
  'reset.done': 'Balance reset: {amount|cr}',

  'kind.market': 'market',
  'kind.walkover': 'last one standing',
  'kind.cancel': 'cancellation',
  'games.emptyPlayer': 'No games yet — take a seat at a table in the lobby.',
  'games.emptyAdmin': 'There have been no games yet.',
  'games.historyTag': 'history',
  'games.error': 'error {v}',
  'games.replay': 'How the game went',
  'col.time': 'Time',
  'col.table': 'Table',
  'col.composition': 'Composition',
  'col.forecast': 'Forecast',
  'col.contributed': 'Contributed',
  'col.net': 'Net result',
  'col.players': 'Players',
  'col.pot': 'Pot',
  'col.commission': 'Commission',
  'col.player': 'Player',
  'col.error': 'Error',
  'col.payout': 'Payout',

  'replay.loading': 'Loading game record…',
  'replay.crumbAdmin': 'All games',
  'replay.crumbProfile': 'Profile',
  'replay.crumbGame': 'Game {id}',
  'replay.title': 'Table {ante|cr} · {date}',
  'replay.modeReplay': 'historical replay',
  'replay.modeLive': 'live',
  'replay.sourceSynthetic': 'synthetic data',
  'replay.seats': '{n} participant|{n} participants',
  'replay.result': 'Outcome',
  'replay.resultCancelled': 'cancelled',
  'replay.resultWalkover': 'last one standing',
  'replay.resultSettled': 'settlement',
  'replay.pot': 'Pot',
  'replay.legacy': 'This record was migrated from a previous version’s log: the move timeline and chart were not stored, only the settlement is available.',
  'replay.toStart': 'To start',
  'replay.stepBack': 'Step back',
  'replay.pause': 'Pause',
  'replay.play': 'Play',
  'replay.stepForward': 'Step forward',
  'replay.toEnd': 'To end',
  'replay.position': 'Point in the record',
  'replay.stageSettled': 'Settlement complete',
  'replay.stagePrep': 'Preparation: antes go into the pot',
  'replay.participants': 'Participants',
  'replay.you': 'you',
  'replay.returned': 'returned {amount}',
  'replay.noteNet': 'On the right — net result.',
  'replay.noteContributed': 'On the right — contributed to the pot so far.',
  'replay.noteAdmin': 'Forecasts are visible to the administrator.',
  'replay.notePlayer': 'Other players’ forecasts are shown in the settlement.',
  'replay.composition': 'Composition',
  'replay.compositionHidden': 'revealed in round 3',
  'replay.timeline': 'Timeline',
  'replay.settlement': 'Settlement',
  'replay.summary': 'P₀ {p0} → P₁ {p1}; commission {commission|cr}.',
  'replay.verify': 'Verify in browser',
  'replay.download': 'Download verification package',
  'replay.roundPrep': 'preparation',
  'replay.round': 'round {n}',
};
